<template>
  <div class="page-card finance-page">
    <div class="page-head">
      <h2 class="page-title">财务流水</h2>
      <div class="page-subtitle">展示已完成的系统入账与支出流水</div>
      <el-alert
        class="page-tip"
        type="info"
        :closable="false"
        show-icon
      >
        <template slot="title">
          不包含商城流水。商城流水请前往
          <router-link to="/platform/mall/product-flow" class="page-tip-link">商城中心 · 商城流水</router-link>
          查看
        </template>
      </el-alert>
    </div>

    <div class="summary-grid">
      <div class="summary-item income">
        <div class="summary-label">入账合计</div>
        <div class="summary-value">¥{{ formatMoney(summary.incomeAmount) }}</div>
      </div>
      <div class="summary-item expense">
        <div class="summary-label">支出合计</div>
        <div class="summary-value">¥{{ formatMoney(summary.expenseAmount) }}</div>
      </div>
      <div class="summary-item net">
        <div class="summary-label">净额</div>
        <div class="summary-value">¥{{ formatMoney(summary.netAmount) }}</div>
      </div>
      <div class="summary-item count">
        <div class="summary-label">流水笔数</div>
        <div class="summary-value">{{ summary.transactionCount || 0 }}</div>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          placeholder="流水号/来源编号/主体/备注"
          clearable
          style="width: 260px"
          @keyup.enter.native="fetchData"
        />
        <el-select v-model="query.direction" clearable placeholder="方向" style="width: 120px">
          <el-option label="入账" :value="1" />
          <el-option label="支出" :value="2" />
        </el-select>
        <el-select v-model="query.sourceType" clearable placeholder="来源" style="width: 180px">
          <el-option label="订单" :value="1" />
          <el-option label="商家曝光值充值" :value="2" />
          <el-option label="会员充值" :value="3" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 260px"
        />
        <el-button type="primary" @click="fetchData">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
        <el-button size="small" :loading="exporting" @click="exportTransactions">导出对账</el-button>
      </div>
    </div>

    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="transactionNo" label="流水号" min-width="190" />
      <el-table-column label="方向" width="90">
        <template slot-scope="{ row }">
          <el-tag :type="row.direction === 1 ? 'success' : 'danger'">{{ row.directionText || '-' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="金额" width="140" align="right">
        <template slot-scope="{ row }">
          <span :class="row.direction === 1 ? 'amount-income' : 'amount-expense'">
            {{ row.direction === 1 ? '+' : '-' }}¥{{ formatMoney(row.amount) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="sourceTypeText" label="来源" min-width="150" />
      <el-table-column prop="sourceNo" label="来源编号" min-width="160">
        <template slot-scope="{ row }">{{ row.sourceNo || row.sourceId || '-' }}</template>
      </el-table-column>
      <el-table-column label="关联主体" min-width="180">
        <template slot-scope="{ row }">
          <div class="subject-cell">
            <span>{{ row.subjectName || '-' }}</span>
            <span class="subject-sub">{{ row.subjectTypeText || '-' }} ID：{{ row.subjectId || '-' }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="occurredAt" label="发生时间" width="180" />
      <el-table-column prop="remark" label="备注" min-width="220" show-overflow-tooltip />
      <el-table-column label="操作" width="90" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openDetail(row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="流水详情" :visible.sync="detailVisible" width="920px">
      <div v-loading="detailLoading" class="transaction-detail-wrap">
        <div v-if="detail.id">
          <el-descriptions title="流水基础信息" :column="2" border class="detail-section">
            <el-descriptions-item label="流水号">{{ detail.transactionNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="发生时间">{{ detail.occurredAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="方向">{{ detail.directionText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="金额">
              <span :class="detail.direction === 1 ? 'amount-income' : 'amount-expense'">
                {{ detail.direction === 1 ? '+' : '-' }}¥{{ formatMoney(detail.amount) }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="来源">{{ detail.sourceTypeText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="来源编号">{{ detail.sourceNo || detail.sourceId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="关联主体">{{ detail.subjectName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="主体ID">{{ detail.subjectTypeText || '-' }} {{ detail.subjectId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="备注" :span="2">{{ detail.remark || '-' }}</el-descriptions-item>
          </el-descriptions>

          <el-descriptions
            v-if="detail.orderDetail"
            title="订单与通告明细"
            :column="2"
            border
            class="detail-section"
          >
            <el-descriptions-item label="订单ID">{{ detail.orderDetail.orderId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="订单编号">{{ detail.orderDetail.orderNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="订单来源">{{ detail.orderDetail.sourceText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="来源编号">{{ detail.orderDetail.sourceBizNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告ID">{{ detail.orderDetail.noticeId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告编号">{{ detail.orderDetail.noticeNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告标题" :span="2">{{ detail.orderDetail.noticeTitle || '-' }}</el-descriptions-item>
            <el-descriptions-item label="商家">{{ detail.orderDetail.merchantName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="商家用户ID">{{ detail.orderDetail.merchantUserId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="艺人">{{ detail.orderDetail.artistName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="艺人用户ID">{{ detail.orderDetail.artistUserId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="履约状态">{{ detail.orderDetail.fulfillmentStatusText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="结算状态">{{ detail.orderDetail.settlementStatusText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="活动开始">{{ detail.orderDetail.activityStartAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="活动结束">{{ detail.orderDetail.activityEndAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="工作城市">{{ detail.orderDetail.workCity || '-' }}</el-descriptions-item>
            <el-descriptions-item label="工作区域">{{ detail.orderDetail.workArea || '-' }}</el-descriptions-item>
            <el-descriptions-item label="详细地点" :span="2">{{ detail.orderDetail.locationDetail || '-' }}</el-descriptions-item>
            <el-descriptions-item label="费用说明" :span="2">{{ detail.orderDetail.salaryDesc || '-' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ detail.orderDetail.createdAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="结算时间">{{ detail.orderDetail.settledAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="关闭状态">{{ detail.orderDetail.closed ? '已关闭' : '未关闭' }}</el-descriptions-item>
            <el-descriptions-item label="关闭时间">{{ detail.orderDetail.closedAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="关闭原因" :span="2">{{ detail.orderDetail.closeReason || '-' }}</el-descriptions-item>
          </el-descriptions>

          <el-descriptions
            v-if="detail.exposureDetail"
            title="曝光充值与通告明细"
            :column="2"
            border
            class="detail-section"
          >
            <el-descriptions-item label="充值ID">{{ detail.exposureDetail.rechargeId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="充值单号">{{ detail.exposureDetail.rechargeNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="商家">{{ detail.exposureDetail.merchantName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="商家用户ID">{{ detail.exposureDetail.merchantUserId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告ID">{{ detail.exposureDetail.noticeId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告编号">{{ detail.exposureDetail.noticeNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告标题" :span="2">{{ detail.exposureDetail.noticeTitle || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告类型">{{ detail.exposureDetail.noticeType || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告状态">{{ detail.exposureDetail.noticeStatusText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="充值金额">¥{{ formatMoney(detail.exposureDetail.amount) }}</el-descriptions-item>
            <el-descriptions-item label="兑换比例">{{ detail.exposureDetail.exchangeRatio || '-' }}</el-descriptions-item>
            <el-descriptions-item label="充值曝光值">{{ detail.exposureDetail.exposureValue || 0 }}</el-descriptions-item>
            <el-descriptions-item label="充值后曝光值">{{ detail.exposureDetail.afterValue || 0 }}</el-descriptions-item>
            <el-descriptions-item label="通告当前曝光值">{{ detail.exposureDetail.noticeExposureValue || 0 }}</el-descriptions-item>
            <el-descriptions-item label="通告累计曝光值">{{ detail.exposureDetail.noticeTotalExposureValue || 0 }}</el-descriptions-item>
            <el-descriptions-item label="活动开始">{{ detail.exposureDetail.activityStartAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="活动结束">{{ detail.exposureDetail.activityEndAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="报名截止">{{ detail.exposureDetail.signupDeadlineAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="充值状态">{{ detail.exposureDetail.statusText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="工作城市">{{ detail.exposureDetail.workCity || '-' }}</el-descriptions-item>
            <el-descriptions-item label="工作区域">{{ detail.exposureDetail.workArea || '-' }}</el-descriptions-item>
            <el-descriptions-item label="详细地点" :span="2">{{ detail.exposureDetail.locationDetail || '-' }}</el-descriptions-item>
            <el-descriptions-item label="费用说明" :span="2">{{ detail.exposureDetail.salaryDesc || '-' }}</el-descriptions-item>
            <el-descriptions-item label="充值来源">{{ detail.exposureDetail.source || '-' }}</el-descriptions-item>
            <el-descriptions-item label="充值时间">{{ detail.exposureDetail.createdAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="备注" :span="2">{{ detail.exposureDetail.remark || '-' }}</el-descriptions-item>
          </el-descriptions>

          <el-descriptions
            v-if="detail.membershipDetail"
            title="会员充值明细"
            :column="2"
            border
            class="detail-section"
          >
            <el-descriptions-item label="会员记录ID">{{ detail.membershipDetail.recordId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="艺人用户ID">{{ detail.membershipDetail.artistUserId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="套餐名称">{{ detail.membershipDetail.planName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="套餐ID">{{ detail.membershipDetail.planId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="会员等级">{{ detail.membershipDetail.membershipLevelText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="周期">{{ detail.membershipDetail.periodTypeText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="原价">¥{{ formatMoney(detail.membershipDetail.originalPrice) }}</el-descriptions-item>
            <el-descriptions-item label="套餐售价">¥{{ formatMoney(detail.membershipDetail.planSalePrice) }}</el-descriptions-item>
            <el-descriptions-item label="优惠合计">¥{{ formatMoney(detail.membershipDetail.discountAmount) }}</el-descriptions-item>
            <el-descriptions-item label="实付金额">¥{{ formatMoney(detail.membershipDetail.payableAmount) }}</el-descriptions-item>
            <el-descriptions-item label="开始时间">{{ detail.membershipDetail.startAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="到期时间">{{ detail.membershipDetail.expireAt || '永久有效' }}</el-descriptions-item>
            <el-descriptions-item label="开通来源">{{ detail.membershipDetail.source || '-' }}</el-descriptions-item>
            <el-descriptions-item label="记录备注">{{ detail.membershipDetail.remark || '-' }}</el-descriptions-item>
          </el-descriptions>

          <div v-if="detail.membershipDetail" class="detail-section">
            <div class="section-title">优惠券明细</div>
            <el-table
              v-if="detail.membershipDetail.couponDetail"
              :data="[detail.membershipDetail.couponDetail]"
              border
              size="small"
            >
              <el-table-column prop="couponNo" label="券编号" min-width="150" />
              <el-table-column prop="couponName" label="优惠券名称" min-width="150" />
              <el-table-column prop="couponTypeText" label="券类型" width="100" />
              <el-table-column prop="useScopeText" label="使用范围" width="110" />
              <el-table-column label="规则" min-width="140">
                <template slot-scope="{ row }">{{ couponRuleText(row) }}</template>
              </el-table-column>
              <el-table-column label="抵扣金额" width="110" align="right">
                <template slot-scope="{ row }">¥{{ formatMoney(row.discountValue) }}</template>
              </el-table-column>
              <el-table-column label="券后实付" width="110" align="right">
                <template slot-scope="{ row }">¥{{ formatMoney(row.finalPayAmount) }}</template>
              </el-table-column>
              <el-table-column prop="usedAt" label="使用时间" width="180" />
            </el-table>
            <el-empty v-else description="本次会员充值未使用优惠券" :image-size="88" />
          </div>

          <el-descriptions
        </div>
        <el-empty v-else-if="!detailLoading" description="暂无流水详情" :image-size="88" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { exportFinanceTransactions, getFinanceTransactionDetail, getFinanceTransactions } from '../../../api/platformManage'

export default {
  name: 'FinanceTransactionsPage',
  data() {
    return {
      loading: false,
      exporting: false,
      detailLoading: false,
      detailVisible: false,
      detail: {},
      list: [],
      summary: {
        incomeAmount: 0,
        expenseAmount: 0,
        netAmount: 0,
        transactionCount: 0
      },
      dateRange: [],
      query: {
        keyword: '',
        direction: undefined,
        sourceType: undefined
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      const params = {
        ...this.query,
        startAt: this.dateRange && this.dateRange.length ? this.dateRange[0] : undefined,
        endAt: this.dateRange && this.dateRange.length ? this.dateRange[1] : undefined
      }
      try {
        const data = await getFinanceTransactions(params)
        this.summary = data.summary || {
          incomeAmount: 0,
          expenseAmount: 0,
          netAmount: 0,
          transactionCount: 0
        }
        this.list = Array.isArray(data.list) ? data.list : []
      } finally {
        this.loading = false
      }
    },
    resetQuery() {
      this.query = {
        keyword: '',
        direction: undefined,
        sourceType: undefined
      }
      this.dateRange = []
      this.fetchData()
    },
    buildExportParams() {
      return {
        keyword: this.query.keyword || undefined,
        direction: this.query.direction,
        sourceType: this.query.sourceType,
        startAt: this.dateRange && this.dateRange.length ? this.dateRange[0] : undefined,
        endAt: this.dateRange && this.dateRange.length ? this.dateRange[1] : undefined
      }
    },
    async exportTransactions() {
      this.exporting = true
      try {
        const response = await exportFinanceTransactions(this.buildExportParams())
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `财务流水对账-${Date.now()}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (error) {
        this.$message.error((error && error.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    async openDetail(row) {
      if (!row || !row.id) return
      this.detailVisible = true
      this.detailLoading = true
      this.detail = {}
      try {
        this.detail = await getFinanceTransactionDetail(row.id)
      } finally {
        this.detailLoading = false
      }
    },
    formatMoney(value) {
      const num = Number(value || 0)
      return num.toFixed(2)
    },
    couponRuleText(row) {
      if (!row) return '-'
      if (row.couponType === 2) {
        return row.discountRate ? `${Number(row.discountRate)}折` : '折扣券'
      }
      if (row.thresholdAmount && Number(row.thresholdAmount) > 0) {
        return `满¥${this.formatMoney(row.thresholdAmount)}减¥${this.formatMoney(row.discountAmount)}`
      }
      if (row.discountAmount && Number(row.discountAmount) > 0) {
        return `立减¥${this.formatMoney(row.discountAmount)}`
      }
      return row.couponTypeText || '-'
    }
  }
}
</script>

<style scoped>
.finance-page {
  min-height: 100%;
}

.page-head {
  margin-bottom: 16px;
}

.page-subtitle {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}

.page-tip {
  margin-top: 12px;
}

.page-tip-link {
  color: #409eff;
  font-weight: 500;
}

.page-tip-link:hover {
  color: #66b1ff;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.summary-item {
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}

.summary-label {
  color: #909399;
  font-size: 13px;
}

.summary-value {
  margin-top: 10px;
  color: #303133;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.income .summary-value,
.amount-income {
  color: #67c23a;
}

.expense .summary-value,
.amount-expense {
  color: #f56c6c;
}

.net .summary-value {
  color: #409eff;
}

.toolbar {
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.subject-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.subject-sub {
  color: #909399;
  font-size: 12px;
}

.transaction-detail-wrap {
  min-height: 120px;
}

.detail-section {
  margin-bottom: 18px;
}

.section-title {
  margin-bottom: 12px;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
